import React, {
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import Api from 'talent_search/lib/api';
import type { Paths } from 'talent_search/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type { RediscoveryProps } from 'talent_matching/types';

export interface RediscoveryTabContextValue {
  apiClient: Api;
  paths: Paths;
  milestones: Item[];
  currencies: Item[];
  focusAttributes: Item[];
  savedSearchOptions: Item[];
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  unfilteredCount: number;
  similarJobOptions: Item[];
  savedFilterSet: Item | null;
  setSavedFilterSet: (ss: Item | null) => void;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
  hiringPlanDepartment: Item | null;
  hiringPlanOffices: Item[];
}

const RediscoveryTabContext = createContext<
  RediscoveryTabContextValue | undefined
>(undefined);

export const useRediscoveryTabContext = () => {
  const context = useContext(RediscoveryTabContext);
  if (!context) {
    throw new Error(
      'useRediscoveryTabContext must be used within a RediscoveryTabContextProvider'
    );
  }
  return context;
};

type RediscoveryTabContextProviderProps = {
  rediscoveryProps: RediscoveryProps;
  children: React.ReactNode;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
};

export const RediscoveryTabContextProvider = ({
  rediscoveryProps,
  children,
  activeSidePanelTab,
  setActiveSidePanelTab,
}: RediscoveryTabContextProviderProps) => {
  const [savedFilterSet, setSavedFilterSet] = useState<Item | null>(null);

  const initialSavedSearchOptions: Item[] = (
    (rediscoveryProps.savedSearchOptions as unknown as Array<{
      label: string;
      value: number;
    }>) ?? []
  ).map((item) => ({
    value: item.value?.toString() ?? '',
    label: item.label,
  }));

  const [savedSearchOptions, setSavedSearchOptions] = useState<Item[]>(
    initialSavedSearchOptions
  );

  const apiClient = useMemo(() => {
    return new Api(rediscoveryProps.paths);
  }, [rediscoveryProps?.paths]);

  const contextValue: RediscoveryTabContextValue = useMemo(
    () => ({
      apiClient,
      paths: rediscoveryProps.paths,
      milestones: rediscoveryProps.milestones,
      currencies: rediscoveryProps.currencies,
      focusAttributes: rediscoveryProps.focusAttributes,
      savedSearchOptions,
      setSavedSearchOptions,
      unfilteredCount: rediscoveryProps.unfilteredCount,
      similarJobOptions: rediscoveryProps.similarJobOptions ?? [],
      savedFilterSet,
      setSavedFilterSet,
      activeSidePanelTab,
      setActiveSidePanelTab,
      hiringPlanDepartment: rediscoveryProps.hiringPlanDepartment ?? null,
      hiringPlanOffices: rediscoveryProps.hiringPlanOffices ?? [],
    }),
    [
      rediscoveryProps,
      apiClient,
      savedSearchOptions,
      savedFilterSet,
      activeSidePanelTab,
      setActiveSidePanelTab,
    ]
  );

  return (
    <RediscoveryTabContext.Provider value={contextValue}>
      {children}
    </RediscoveryTabContext.Provider>
  );
};
